/**
 * Descriptor variant resolution.
 * Classifies a programme as a QQI FET/Component Award Scheme (CAS) award or a
 * Higher Education (non-CAS) award so the descriptor export can route to the
 * matching pathway.
 * @module export/descriptor-variant
 */

import type { Programme } from "@/types";
import { isFeAwardType, isHeAwardType } from "@/utils/award-types";

/** Descriptor pathway variant. */
export type DescriptorVariant = "cas" | "non-cas";

/**
 * Resolves the descriptor variant for a programme.
 *
 * The FE/CAS decision is driven by award-type data and `casMajorAwardCode`
 * rather than NFQ level alone:
 * - CAS (QQI FET / Component Award Scheme) when the award type is an FE award
 *   (`isFeAwardType`);
 * - non-CAS (Higher Education) when the award type is an HE award
 *   (`isHeAwardType`), whatever NFQ level or CAS code it carries, since a code
 *   survives a switch from FE to HE;
 * - for award types in neither list, a `casMajorAwardCode` or NFQ level 6 or
 *   below means CAS.
 *
 * Otherwise it is classified as `non-cas`.
 */
export function resolveDescriptorVariant(programme: Programme): DescriptorVariant {
  const awardType = programme.awardType ?? "";
  if (isFeAwardType(awardType)) {
    return "cas";
  }
  if (isHeAwardType(awardType)) {
    return "non-cas";
  }
  if ((programme.casMajorAwardCode ?? "").trim() !== "") {
    return "cas";
  }
  const nfqLevel = programme.nfqLevel;
  if (typeof nfqLevel === "number" && nfqLevel <= 6) {
    return "cas";
  }
  return "non-cas";
}
